import React, { useState, useEffect } from 'react';
import axios from 'axios';
import colors, { shadows } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function UserManagementPage() {
    const [users, setUsers] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddUser, setShowAddUser] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [newUser, setNewUser] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'member'
    });

    useEffect(() => {
        fetchUsers();
        fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getAuthHeader = () => {
        const token = localStorage.getItem('token');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/users`, {
                headers: getAuthHeader()
            });
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganization = async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/organization`, {
                headers: getAuthHeader()
            });
            if (response.data.success) {
                setOrganization(response.data.data);
            }
        } catch (err) {
            console.error('Failed to load organization');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            // Combine first and last name
            const fullName = `${newUser.firstName} ${newUser.lastName}`.trim();
            
            const response = await axios.post(
                `${API_URL}/auth/register`,
                {
                    name: fullName,
                    email: newUser.email,
                    password: newUser.password,
                    role: newUser.role
                },
                { headers: getAuthHeader() }
            );

            if (response.data.success) {
                setSuccess(`User ${fullName} added successfully!`);
                setNewUser({ firstName: '', lastName: '', email: '', password: '', role: 'member' });
                setShowAddUser(false);
                fetchUsers(); // Refresh user list
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add user');
        }
    };

    const handleChangeRole = async (userId, newRole) => {
        try {
            const response = await axios.put(
                `${API_URL}/auth/users/${userId}/role`,
                { role: newRole },
                { headers: getAuthHeader() }
            );

            if (response.data.success) {
                setSuccess('User role updated successfully!');
                fetchUsers();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update role');
        }
    };

    const handleDeactivateUser = async (userId) => {
        if (!window.confirm('Are you sure you want to deactivate this user?')) {
            return;
        }

        try {
            const response = await axios.put(
                `${API_URL}/auth/users/${userId}/deactivate`,
                {},
                { headers: getAuthHeader() }
            );

            if (response.data.success) {
                setSuccess('User deactivated successfully!');
                fetchUsers();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to deactivate user');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '2rem'
            }}>
                <div>
                    <h1 style={{ marginBottom: '0.5rem' }}>Team Management</h1>
                    {organization && (
                        <p style={{ color: colors.gray[600], fontSize: '0.95rem' }}>
                            {organization.name} • {users.length} / {organization.max_users} users • {organization.plan_type} plan
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setShowAddUser(!showAddUser)}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: colors.blue[500],
                        color: colors.white,
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                    }}
                >
                    {showAddUser ? 'Cancel' : '+ Add User'}
                </button>
            </div>

            {error && (
                <div style={{
                    padding: '1rem',
                    backgroundColor: colors.red[50],
                    color: colors.red[600],
                    borderRadius: '6px',
                    marginBottom: '1rem'
                }}>
                    {error}
                </div>
            )}

            {success && (
                <div style={{
                    padding: '1rem',
                    backgroundColor: colors.green[100],
                    color: colors.green[700],
                    borderRadius: '6px',
                    marginBottom: '1rem'
                }}>
                    {success}
                </div>
            )}

            {/* Add User Form */}
            {showAddUser && (
                <div style={{
                    backgroundColor: colors.white,
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: shadows.lg,
                    marginBottom: '2rem',
                    border: `1px solid ${colors.gray[200]}`
                }}>
                    <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>
                        Add New Team Member
                    </h2>
                    <form onSubmit={handleAddUser}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.gray[700]
                                }}>
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    value={newUser.firstName}
                                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                                    required
                                    placeholder="John"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.blue[500]}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.gray[700]
                                }}>
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    value={newUser.lastName}
                                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                                    required
                                    placeholder="Smith"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.blue[500]}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>
                        </div>
                        
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                color: colors.gray[700]
                            }}>
                                Email *
                            </label>
                            <input
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                required
                                placeholder="john.smith@company.com"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: `2px solid ${colors.gray[200]}`,
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = colors.blue[500]}
                                onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.gray[700]
                                }}>
                                    Password *
                                </label>
                                <input
                                    type="password"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    required
                                    minLength="8"
                                    placeholder="Min 8 characters"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = colors.blue[500]}
                                    onBlur={(e) => e.target.style.borderColor = colors.gray[200]}
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.gray[700]
                                }}>
                                    Role *
                                </label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: colors.white,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value="member">Member</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <button
                                type="submit"
                                style={{
                                    padding: '0.875rem 2rem',
                                    backgroundColor: colors.green[500],
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                                onMouseOut={(e) => e.target.style.backgroundColor = colors.green[500]}
                            >
                                Add User
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAddUser(false);
                                    setNewUser({ firstName: '', lastName: '', email: '', password: '', role: 'member' });
                                }}
                                style={{
                                    padding: '0.875rem 2rem',
                                    backgroundColor: colors.gray[100],
                                    color: colors.gray[700],
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = colors.gray[200]}
                                onMouseOut={(e) => e.target.style.backgroundColor = colors.gray[100]}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Users List */}
            <div style={{
                backgroundColor: colors.white,
                borderRadius: '8px',
                boxShadow: shadows.sm,
                overflow: 'hidden'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: colors.gray[50] }}>
                        <tr>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Name</th>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Email</th>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Role</th>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id} style={{ borderTop: `1px solid ${colors.gray[200]}` }}>
                                <td style={{ padding: '1rem' }}>{user.name}</td>
                                <td style={{ padding: '1rem' }}>{user.email}</td>
                                <td style={{ padding: '1rem' }}>
                                    <select
                                        value={user.role}
                                        onChange={(e) => handleChangeRole(user.id, e.target.value)}
                                        style={{
                                            padding: '0.5rem',
                                            border: `1px solid ${colors.gray[300]}`,
                                            borderRadius: '4px',
                                            backgroundColor: colors.white,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="member">Member</option>
                                        <option value="manager">Manager</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                    <span style={{
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '12px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        backgroundColor: user.is_active ? colors.green[100] : colors.red[50],
                                        color: user.is_active ? colors.green[700] : colors.red[600]
                                    }}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td style={{ padding: '1rem' }}>
                                    {user.is_active && (
                                        <button
                                            onClick={() => handleDeactivateUser(user.id)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: colors.red[700],
                                                color: colors.white,
                                                border: 'none',
                                                borderRadius: '4px',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Deactivate
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default UserManagementPage;